import type { Action, FightCommand, Hand } from '../rules/types';
import { GestureReader } from './gestures';
import { isTyping } from './typing';

/**
 * The player's hands: keys under the left hand (by their place on the keyboard, whatever its language), and the mouse or
 * a finger through the gesture reader. The left column of keys punches with the left hand, the right column with the right.
 */
const keys: Record<string, { hand: Hand | 'alternate'; action: Action }> = {
  KeyA: { hand: 'left', action: 'straight' },
  KeyD: { hand: 'right', action: 'straight' },
  KeyQ: { hand: 'left', action: 'hook' },
  KeyE: { hand: 'right', action: 'hook' },
  KeyZ: { hand: 'left', action: 'uppercut' },
  KeyC: { hand: 'right', action: 'uppercut' },
  KeyS: { hand: 'alternate', action: 'bodyBlow' },
  KeyW: { hand: 'right', action: 'super' },
  KeyX: { hand: 'left', action: 'dodge' },
};

export class Controls {
  private readonly gestures: GestureReader;
  private blockKey = false;
  private nextBodyBlow: Hand = 'left';

  constructor(surface: HTMLElement, private readonly send: (command: FightCommand) => void) {
    this.gestures = new GestureReader(send);
    window.addEventListener('keydown', (event) => this.key(event, true));
    window.addEventListener('keyup', (event) => this.key(event, false));
    // Presses on the HUD's buttons never reach the fighting surface, so they are never gestures.
    const at = (event: PointerEvent) => [event.pointerId, event.timeStamp / 1000, event.clientX / surface.clientWidth, 1 - event.clientY / surface.clientHeight] as const;
    surface.addEventListener('pointerdown', (event) => {
      this.gestures.press(...at(event));
      // Keeps the moves and the release coming if the finger slides off the canvas.
      surface.setPointerCapture(event.pointerId);
    });
    surface.addEventListener('pointermove', (event) => this.gestures.move(...at(event)));
    surface.addEventListener('pointerup', (event) => this.gestures.release(...at(event)));
    surface.addEventListener('pointercancel', (event) => this.gestures.release(...at(event)));
    surface.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  /** Every frame: a finger that has stayed still long enough starts blocking, though it sends no events. */
  update(): void {
    // Pointer events are stamped on the page's clock, the same one performance.now reads.
    this.gestures.update(performance.now() / 1000);
  }

  /** Space held, or a still press: a block for as long as it lasts. */
  get blockHeld(): boolean {
    return this.blockKey || this.gestures.isBlocking;
  }

  private key(event: KeyboardEvent, down: boolean): void {
    if (isTyping(event)) return;
    if (event.code === 'Space') {
      this.blockKey = down;
      event.preventDefault();
      return;
    }
    const binding = keys[event.code];
    if (!binding || !down || event.repeat) return;
    event.preventDefault();
    let hand: Hand;
    if (binding.hand === 'alternate') {
      hand = this.nextBodyBlow;
      this.nextBodyBlow = hand === 'left' ? 'right' : 'left';
    } else {
      hand = binding.hand;
    }
    this.send({ hand, action: binding.action });
  }
}
